import { useTranslation } from 'react-i18next';

import { useUpdateRecurrenceInboxEntry as useUpdateRecurrenceService } from '../../services/recurrenceInboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import { useDebouncedIdSetPersistence } from '../useDebouncedIdSetPersistence';

interface UseRecurrenceEntryLabelManagerParams {
  recurrenceTaskGroupId: string;
  recurrenceId: string;
  initialSelectedIds: string[];
}

interface UseRecurrenceEntryLabelManagerResult {
  selectedIds: string[];
  handleToggle: (labelId: string) => void;
}

function applyToStore(recurrenceId: string, taskLabelIds: string[]) {
  const { recurrenceInboxEntries, updateRecurrenceInboxEntry } =
    useInboxEntriesStore.getState().taskManager.activeTaskGroup
      .recurrenceEntries;
  const entry = recurrenceInboxEntries.find((e) => e.id === recurrenceId);
  if (entry) {
    updateRecurrenceInboxEntry({ ...entry, taskLabelIds });
  }
}

export function useRecurrenceEntryLabelManager({
  recurrenceTaskGroupId,
  recurrenceId,
  initialSelectedIds,
}: UseRecurrenceEntryLabelManagerParams): UseRecurrenceEntryLabelManagerResult {
  const { t } = useTranslation();
  const { updateRecurrenceInboxEntry: updateService } =
    useUpdateRecurrenceService();

  const { selectedIds, handleToggle } = useDebouncedIdSetPersistence({
    initialIds: initialSelectedIds,
    onOptimisticChange: (newIds) => applyToStore(recurrenceId, newIds),
    persist: async ({ currentIds }) => {
      await updateService({
        taskGroupId: recurrenceTaskGroupId,
        id: recurrenceId,
        taskLabelIds: currentIds,
      });
    },
    onPersistError: (revertIds) => applyToStore(recurrenceId, revertIds),
    errorToastMessage: t('doBox.recurrences.updateError'),
  });

  return { selectedIds, handleToggle };
}
